// inline-markdown — the three inline markdown forms the course actually authors, rendered
// safely for teacher-authored text that reaches the isolated editor surfaces.
//
// WHY THIS EXISTS. Step prompts, hints and challenge requirements have always been written
// in markdown, but every one of those surfaces rendered them through `textContent`, so a
// student saw the literal punctuation — "the **value** you want gone", "call it `alien`".
// Measured across scripts/year10 on 2026-08-22: 270 inline-code spans and 49 bold in
// guided-make prompts/hints, plus 13 code spans across challenge requirement text and hints.
// Fixing the renderers once beats editing a hundred-odd authored strings, which would only
// have left the next author copying a neighbour and reintroducing it.
//
// ⚠️⚠️ ESCAPE FIRST, WHITELIST SECOND — THAT ORDER IS THE ENTIRE SAFETY PROPERTY.
// This text is teacher-authored and arrives from the server, so it is untrusted. Every `&`,
// `<` and `>` is escaped BEFORE a single tag exists, so the only tags that can ever reach the
// DOM are the <code>/<strong>/<em> written below. That keeps the "safe" half of the original
// `textContent` decision while dropping the "no markdown dep" half — and it is why this is
// twenty lines of regex rather than `marked`, which would be a new dependency in the editor
// bundle AND would still need sanitising afterwards.
//
// ⚠️ NEWLINES ARE CSS, NOT MARKUP. Every consumer keeps `white-space: pre-wrap` on its
// container, so paragraph breaks behave exactly as they did before this module existed.
// Do NOT emit <br> or <p> here — that would double every gap and silently re-lay-out around
// a hundred existing activities.
//
// ⚠️ BLOCK MARKDOWN IS NOT SUPPORTED AND MUST NOT BE ADDED CASUALLY. There are no external links,
// headings or blockquotes in any authored prompt (measured); the one link form is a same-site [text](/path). A `>` at the start of a line —
// M4.23 step 3 has one — renders as a literal `>`, exactly as it did before. If block
// support is ever wanted, that is a design decision about authoring, not a bug fix.

// Sentinel for lifting code spans out before emphasis runs, so a `**` inside backticks is
// never treated as bold. It is stripped from the input first, so nothing in authored text
// can forge one. Deliberately a named constant — an earlier revision embedded literal NUL
// bytes in the source, which git treats as binary.
var MD_SENTINEL = String.fromCharCode(0);

export function inlineMarkdown(src) {
  var text = String(src == null ? '' : src).split(MD_SENTINEL).join('');
  var out = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  var spans = [];
  out = out.replace(/`([^`\n]+)`/g, function (_m, body) {
    return MD_SENTINEL + (spans.push(body) - 1) + MD_SENTINEL;
  });
  // One narrow link form: [text](/path) to a page on THIS site, opened in a new tab so an activity
  // in progress is not lost. The href must start with a single `/` (no `//`, no scheme), so an
  // authored external or script URL stays literal text. Runs after the code spans are lifted out.
  out = out.replace(/\[([^\]\n]+)\]\((\/(?!\/)[A-Za-z0-9\-_\/.#?=&;]*)\)/g,
    '<a href="$2" target="_blank" rel="noopener">$1</a>');
  out = out
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
  return out.replace(new RegExp(MD_SENTINEL + '(\\d+)' + MD_SENTINEL, 'g'), function (_m, i) {
    return '<code>' + spans[Number(i)] + '</code>';
  });
}
